import React, { useState } from "react";
import {
    FiImage,
    FiChevronLeft,
    FiChevronRight,
    FiMaximize2
} from "react-icons/fi";
import {
    DocSectionCard,
    DocSectionHeader,
    DocSectionBadge,
    DocSectionTitle,
    DocSectionSubtitle,
    DocParagraph,
    DocFormulaPill,
    DocPivotCard,
    DocPivotHeader,
    DocPivotBadge,
    InvalidationAlert,
    KeyPrincipleBox,
    KeyPrincipleRow,
    TimeframeSpectrumCard,
    TimeframeSpectrumHeader,
    TimeframeSpectrumTitle,
    TimeframeLegend,
    TimeframeTrack,
    TimeframePill,
    TimeframeArrow,
    TimeframeStepRuleBox,
    VolatilityVsMomentumCard,
    DocBulletList,
    DocBulletItem,
    DocSubSectionHeader,
    DocSubSectionBadge,
    DocSubSectionTitle,
    DocInlineCarouselCard,
    DocInlineCarouselHeader,
    DocInlineCarouselBadge,
    DocInlineCarouselCaptionTag,
    DocInlineCarouselImageArea,
    DocTableWrapper,
    DocTable,
    DocTh,
    DocTr,
    DocTd,
    ApplicationBox,
    ApplicationCard,
    ApplicationNumberBadge,
    ApplicationBulletBadge,
    ApplicationContent,
    DiagramContainer
} from "./dictionaryStyles";
import {
    HighlightText,
    renderInlineMarkdown
} from "./dictionaryTagHelpers";

export const DocInlineIllustrationCarousel = ({ images, onOpenLightbox, query }) => {
    const [currentIndex, setCurrentIndex] = useState(0);

    if (!images || images.length === 0) return null;

    const currentImg = images[currentIndex] || images[0];

    const handlePrev = (e) => {
        e.stopPropagation();
        setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
    };

    const handleNext = (e) => {
        e.stopPropagation();
        setCurrentIndex((prev) => (prev + 1) % images.length);
    };

    return (
        <DocInlineCarouselCard>
            <DocInlineCarouselHeader>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0, flexWrap: 'wrap' }}>
                    <DocInlineCarouselBadge>
                        <FiImage size={13} color="#ffffff" />
                    </DocInlineCarouselBadge>
                    <DocSectionTitle style={{ fontSize: '0.88rem', margin: 0, display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <span>Illustration</span>
                        {currentImg.caption && (
                            <DocInlineCarouselCaptionTag title={currentImg.caption}>
                                {renderInlineMarkdown(currentImg.caption, query)}
                            </DocInlineCarouselCaptionTag>
                        )}
                    </DocSectionTitle>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                    {images.length > 1 && (
                        <div style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            background: 'rgba(255, 255, 255, 0.05)',
                            padding: '2px 6px',
                            borderRadius: '20px',
                            border: '1px solid rgba(255, 255, 255, 0.1)'
                        }}>
                            <button
                                onClick={handlePrev}
                                title="Image précédente (<)"
                                style={{
                                    background: 'rgba(255, 255, 255, 0.08)',
                                    border: '1px solid rgba(255, 255, 255, 0.12)',
                                    color: '#e2e8f0',
                                    borderRadius: '50%',
                                    width: '22px',
                                    height: '22px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    padding: 0
                                }}
                            >
                                <FiChevronLeft size={13} />
                            </button>
                            <span style={{ fontSize: '0.75rem', fontWeight: '700', color: '#38bdf8', minWidth: '34px', textAlign: 'center' }}>
                                {currentIndex + 1} / {images.length}
                            </span>
                            <button
                                onClick={handleNext}
                                title="Image suivante (>)"
                                style={{
                                    background: 'rgba(255, 255, 255, 0.08)',
                                    border: '1px solid rgba(255, 255, 255, 0.12)',
                                    color: '#e2e8f0',
                                    borderRadius: '50%',
                                    width: '22px',
                                    height: '22px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    padding: 0
                                }}
                            >
                                <FiChevronRight size={13} />
                            </button>
                        </div>
                    )}

                    <button
                        onClick={() => onOpenLightbox && onOpenLightbox(currentImg.url)}
                        title="Plein écran"
                        aria-label="Plein écran"
                        style={{
                            background: 'rgba(76, 206, 172, 0.12)',
                            border: '1px solid rgba(76, 206, 172, 0.3)',
                            color: '#4cceac',
                            borderRadius: '6px',
                            width: '28px',
                            height: '28px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                        }}
                    >
                        <FiMaximize2 size={13} />
                    </button>
                </div>
            </DocInlineCarouselHeader>

            <DocInlineCarouselImageArea
                onClick={() => onOpenLightbox && onOpenLightbox(currentImg.url)}
                title="Cliquer pour agrandir"
            >
                <img src={currentImg.url} alt={currentImg.caption || 'Illustration'} loading="lazy" />
            </DocInlineCarouselImageArea>
        </DocInlineCarouselCard>
    );
};

export const formatTitleWithSubtitle = (titleStr) => {
    if (!titleStr || typeof titleStr !== 'string') return titleStr;
    if (titleStr.includes('*')) return titleStr;
    const match = titleStr.match(/^(.*?)(\s*[—–]\s*|:\s+)(.+)$/);
    if (match) {
        return `${match[1]}${match[2]}*${match[3]}*`;
    }
    return titleStr;
};

export const renderStrategicApplication = (application, query) => {
    if (!application) return null;

    const trimmed = application.trim();

    // Check if it contains a numbered list (e.g. "1. ", "2. ")
    const hasNumberedList = /(?:^|\n)\s*\d+\.\s+/.test(trimmed);
    if (hasNumberedList) {
        const rawItems = trimmed.split(/(?:^|\n)(?=\s*\d+\.\s+)/).map(s => s.trim()).filter(Boolean);
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', width: '100%' }}>
                {rawItems.map((item, idx) => {
                    const match = item.match(/^\s*(\d+)\.\s+([\s\S]*)/);
                    const num = match ? match[1] : String(idx + 1);
                    const content = match ? match[2].trim() : item;
                    return (
                        <ApplicationCard key={idx}>
                            <ApplicationNumberBadge>{num}</ApplicationNumberBadge>
                            <ApplicationContent>
                                {renderInlineMarkdown(content, query)}
                            </ApplicationContent>
                        </ApplicationCard>
                    );
                })}
            </div>
        );
    }

    // Check if it contains bullet points (e.g. "- ", "* ")
    const hasBulletList = /(?:^|\n)\s*[-*]\s+/.test(trimmed);
    if (hasBulletList) {
        const rawItems = trimmed.split(/(?:^|\n)(?=\s*[-*]\s+)/).map(s => s.trim()).filter(Boolean);
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
                {rawItems.map((item, idx) => {
                    const content = item.replace(/^[-*]\s+/, '').trim();
                    return (
                        <ApplicationCard key={idx}>
                            <ApplicationBulletBadge>◆</ApplicationBulletBadge>
                            <ApplicationContent>
                                {renderInlineMarkdown(content, query)}
                            </ApplicationContent>
                        </ApplicationCard>
                    );
                })}
            </div>
        );
    }

    // Check if multiple paragraphs separated by empty lines or newlines
    const paragraphs = trimmed.split(/\n\s*\n/).map(s => s.trim()).filter(Boolean);
    if (paragraphs.length > 1) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', width: '100%' }}>
                {paragraphs.map((p, idx) => (
                    <ApplicationBox key={idx}>
                        <span style={{ fontSize: '1rem', flexShrink: 0, marginTop: '2px' }}>💡</span>
                        <ApplicationContent>
                            {renderInlineMarkdown(p, query)}
                        </ApplicationContent>
                    </ApplicationBox>
                ))}
            </div>
        );
    }

    // Single paragraph or note
    return (
        <ApplicationBox>
            <span style={{ fontSize: '1rem', flexShrink: 0, marginTop: '2px' }}>💡</span>
            <ApplicationContent>
                {renderInlineMarkdown(trimmed, query)}
            </ApplicationContent>
        </ApplicationBox>
    );
};

export const DocRichViewer = ({ content, query, onOpenLightbox, illustrationNode, insertIllustrationBeforeTitle }) => {
    if (!content) return null;

    const isLevelClassificationDoc = content.includes('Chart-Based Levels') && content.includes('Contextual Levels');
    const hasHeadings = content.includes('### ') || content.includes('## ') || /^\s*\d+\.\s+\*\*/m.test(content);

    if (!hasHeadings) {
        return (
            <DocSectionCard>
                <DocParagraph>
                    {renderInlineMarkdown(content, query)}
                </DocParagraph>
            </DocSectionCard>
        );
    }

    let rawSections = [];
    if (/(?:^|\n)#{2,3} (?!#)/.test(content)) {
        rawSections = content.split(/(?=(?:^|\n)#{2,3} (?!#))/g).map(s => s.trim()).filter(Boolean);
    } else {
        rawSections = content.split(/(?=^\s*\d+\.\s+\*\*)/m).map(s => s.trim()).filter(Boolean);
    }

    const badgeGradients = [
        'linear-gradient(135deg, #10b981, #059669)',
        'linear-gradient(135deg, #0ea5e9, #0284c7)',
        'linear-gradient(135deg, #8b5cf6, #6d28d9)',
        'linear-gradient(135deg, #f59e0b, #d97706)',
        'linear-gradient(135deg, #ec4899, #be185d)',
        'linear-gradient(135deg, #ef4444, #dc2626)',
    ];

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {rawSections.map((secStr, secIdx) => {
                let title = "";
                let body = secStr;

                if (secStr.startsWith('### ') || secStr.startsWith('## ')) {
                    const prefixLen = secStr.startsWith('### ') ? 4 : 3;
                    const firstNewline = secStr.indexOf('\n');
                    if (firstNewline !== -1) {
                        title = secStr.substring(prefixLen, firstNewline).trim();
                        body = secStr.substring(firstNewline + 1).trim();
                    } else {
                        title = secStr.substring(prefixLen).trim();
                        body = "";
                    }
                } else {
                    const match = secStr.match(/^\s*(\d+\.\s+\*\*[^*]+\*\*):?(.*)/);
                    if (match) {
                        title = match[1].replace(/\*\*/g, '').trim();
                        body = (match[2] + '\n' + secStr.substring(match[0].length)).trim();
                    }
                }

                let numLabel = String(secIdx + 1).padStart(2, '0');
                let cleanTitle = title;
                const numMatch = title.match(/^((?:PART\s+[IVXLCDM]+|[IVXLCDM]+|\d+(?:\.\d+)?))[.\s—–-]+(.*)/i);
                if (numMatch) {
                    const rawNum = numMatch[1].replace(/^PART\s+/i, '').trim();
                    numLabel = /^\d+$/.test(rawNum) ? (rawNum.includes('.') ? rawNum : String(rawNum).padStart(2, '0')) : rawNum.toUpperCase();
                    cleanTitle = numMatch[2].trim();
                }

                const badgeBg = badgeGradients[secIdx % badgeGradients.length];

                // Protect code blocks (```...```) so blank lines inside them do not split the block
                const codeBlockPlaceholders = [];
                const protectedBody = body.replace(/```[\s\S]*?```/g, (match) => {
                    const placeholder = `__CODE_BLOCK_PLACEHOLDER_${codeBlockPlaceholders.length}__`;
                    codeBlockPlaceholders.push(match);
                    return placeholder;
                });

                const rawBlocksList = protectedBody
                    .split(/\n\s*\n/)
                    .map(b => b.trim())
                    .filter(Boolean)
                    .map(b => b.replace(/__CODE_BLOCK_PLACEHOLDER_(\d+)__/g, (_, idx) => codeBlockPlaceholders[Number(idx)]));

                const rawBlocks = [];
                for (let i = 0; i < rawBlocksList.length; i++) {
                    const cur = rawBlocksList[i];
                    const isGateHeading = /^\s*[-*]?\s*(?:\*\*)?(?:[^*:\n\r]*(?:Gate|⛩\uFE0F?))(?::?\*\*|\*\*:?|:)?\s*$/i.test(cur);
                    if (
                        isGateHeading &&
                        i + 1 < rawBlocksList.length &&
                        !rawBlocksList[i + 1].startsWith('#') &&
                        !rawBlocksList[i + 1].startsWith('---') &&
                        !/^\s*\d+\.\s+/.test(rawBlocksList[i + 1])
                    ) {
                        let combined = cur + '\n' + rawBlocksList[i + 1];
                        i++;
                        while (
                            i + 1 < rawBlocksList.length &&
                            !rawBlocksList[i + 1].startsWith('#') &&
                            !rawBlocksList[i + 1].startsWith('---') &&
                            !/^\s*\d+\.\s+/.test(rawBlocksList[i + 1]) &&
                            !/^\s*[-*]?\s*(?:\*\*)?(?:[^*:\n\r]*(?:Gate|⛩\uFE0F?))(?::?\*\*|\*\*:?|:)?\s*$/i.test(rawBlocksList[i + 1])
                        ) {
                            combined += '\n' + rawBlocksList[i + 1];
                            i++;
                        }
                        rawBlocks.push(combined);
                    } else {
                        rawBlocks.push(cur);
                    }
                }

                let subtitle = "";
                if (rawBlocks.length > 0) {
                    const subMatch = rawBlocks[0].match(/^\*([^*\n\r]+)\*$/);
                    if (subMatch) {
                        subtitle = subMatch[1].trim();
                        rawBlocks.shift();
                    }
                }
                const blocks = [];
                rawBlocks.forEach(rawBlock => {
                    let cleanedRawBlock = rawBlock;
                    if (cleanedRawBlock.startsWith('>')) {
                        cleanedRawBlock = cleanedRawBlock.split('\n').map(l => l.replace(/^>\s?/, '')).join('\n').trim();
                    }
                    let parts = [cleanedRawBlock];
                    if (cleanedRawBlock.startsWith('#### ')) {
                        const firstNl = cleanedRawBlock.indexOf('\n');
                        if (firstNl !== -1) {
                            parts = [
                                cleanedRawBlock.slice(0, firstNl).trim(),
                                cleanedRawBlock.slice(firstNl + 1).trim()
                            ];
                        }
                    }

                    const expandedParts = [];
                    parts.forEach(part => {
                        const bulletMatch = part.match(/\n\s*(?:[-*]|\d+\.)\s+/);
                        const isRuleCard = /Gate\b|⛩\uFE0F?|Decision Gate|Temporal Consistency|Temporal Symmetry|Multi-TF Rule|Multi-Timeframe Rule/i.test(part);
                        if (bulletMatch && !part.startsWith('- ') && !part.startsWith('* ') && !part.startsWith('#### ') && !part.startsWith('```') && !/^\s*\d+\.\s+/.test(part) && !isRuleCard) {
                            const splitIdx = bulletMatch.index;
                            expandedParts.push(part.slice(0, splitIdx).trim());
                            expandedParts.push(part.slice(splitIdx + 1).trim());
                        } else {
                            expandedParts.push(part);
                        }
                    });

                    expandedParts.forEach(part => {
                        if (part.includes('```')) {
                            const subChunks = part.split(/(```[\s\S]*?```)/g).map(s => s.trim()).filter(Boolean);
                            blocks.push(...subChunks);
                        } else {
                            blocks.push(part);
                        }
                    });
                });

                const isInsertBeforeThis = illustrationNode && insertIllustrationBeforeTitle && cleanTitle.toLowerCase().includes(insertIllustrationBeforeTitle.toLowerCase());

                const isMultiTfSection = /multi\s*[-–—]?\s*t(?:ime)?f(?:rame)?|where to find/i.test(title || cleanTitle);
                let sectionId = undefined;
                if (isMultiTfSection) {
                    const combined = ((title || "") + " " + (subtitle || "") + " " + (body || "").slice(0, 200)).toLowerCase();
                    if (combined.includes('chart pattern')) {
                        sectionId = "doc-section-multi-tf-chart";
                    } else if (combined.includes('candlestick')) {
                        sectionId = "doc-section-multi-tf-candle";
                    } else {
                        sectionId = "doc-section-multi-tf";
                    }
                } else {
                    const cleanLower = (cleanTitle || title || "").toLowerCase();
                    if (cleanLower.includes('on-chart rule') || cleanLower.includes('on chart rule')) {
                        sectionId = "doc-section-on-chart-rule";
                    } else if (cleanLower === 'application' || cleanLower.startsWith('application')) {
                        sectionId = "doc-section-application";
                    } else if (cleanLower.startsWith('volatility') || cleanLower.includes('volatility:')) {
                        sectionId = "doc-section-volatility";
                    } else if (cleanLower.startsWith('momentum') || cleanLower.includes('momentum:')) {
                        sectionId = "doc-section-momentum";
                    } else if (cleanLower.includes('cross-asset swing') || cleanLower.includes('cross asset swing') || cleanLower.includes('multi-hour grid')) {
                        sectionId = "doc-section-volatility-grid-swing";
                    } else if (cleanLower.includes('chart-based levels') || cleanLower.includes('chart based levels')) {
                        sectionId = "doc-section-chart-based-levels";
                    }
                }

                return (
                    <React.Fragment key={secIdx}>
                        {isInsertBeforeThis && illustrationNode}
                        <DocSectionCard
                            id={sectionId}
                            data-section={isMultiTfSection ? "multi-tf" : (sectionId ? sectionId.replace('doc-section-', '') : undefined)}
                        >
                            {title && (
                                <DocSectionHeader>
                                    <DocSectionBadge badgeBg={badgeBg}>{numLabel}</DocSectionBadge>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                        <DocSectionTitle>{renderInlineMarkdown(formatTitleWithSubtitle(cleanTitle), query)}</DocSectionTitle>
                                        {subtitle && (
                                            <DocSectionSubtitle $isWarning={subtitle.toLowerCase().includes('watch') || subtitle.toLowerCase().includes('do not plot')}>
                                                {(subtitle.toLowerCase().includes('watch') || subtitle.toLowerCase().includes('do not plot')) && <span>👁️</span>}
                                                {renderInlineMarkdown(`*${subtitle}*`, query)}
                                            </DocSectionSubtitle>
                                        )}
                                    </div>
                                </DocSectionHeader>
                            )}

                            <div>
                                {blocks.map((block, bIdx) => {
                                    if (/M1\s*→\s*M5\s*→\s*M15/.test(block)) {
                                        const tfList = [
                                            { label: 'M1', tier: 'micro' },
                                            { label: 'M5', tier: 'micro' },
                                            { label: 'M15', tier: 'micro' },
                                            { label: 'M30', tier: 'micro' },
                                            { label: 'H1', tier: 'mid' },
                                            { label: 'H4', tier: 'mid' },
                                            { label: 'D1', tier: 'mid' },
                                            { label: 'W1', tier: 'macro' },
                                            { label: 'M', tier: 'macro' },
                                            { label: '3M', tier: 'macro' },
                                            { label: '6M', tier: 'macro' },
                                            { label: 'Y', tier: 'macro' },
                                        ];

                                        const getTierStyle = (tier) => {
                                            if (tier === 'micro') {
                                                return {
                                                    bg: 'rgba(56, 189, 248, 0.12)',
                                                    color: '#38bdf8',
                                                    border: 'rgba(56, 189, 248, 0.35)',
                                                    glow: 'rgba(56, 189, 248, 0.1)'
                                                };
                                            }
                                            if (tier === 'mid') {
                                                return {
                                                    bg: 'rgba(168, 85, 247, 0.12)',
                                                    color: '#c084fc',
                                                    border: 'rgba(168, 85, 247, 0.35)',
                                                    glow: 'rgba(168, 85, 247, 0.1)'
                                                };
                                            }
                                            return {
                                                bg: 'rgba(245, 158, 11, 0.12)',
                                                color: '#fbbf24',
                                                border: 'rgba(245, 158, 11, 0.35)',
                                                glow: 'rgba(245, 158, 11, 0.1)'
                                            };
                                        };

                                        const hasStepRule = block.includes('Reference / Bias TF') || block.includes('Observation TF');

                                        return (
                                            <TimeframeSpectrumCard key={bIdx}>
                                                <TimeframeSpectrumHeader>
                                                    <TimeframeSpectrumTitle>
                                                        <span>⏱️</span>
                                                        <span>Timeframe Continuum</span>
                                                    </TimeframeSpectrumTitle>
                                                    <TimeframeLegend>
                                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: '#38bdf8' }}>
                                                            ● Intraday
                                                        </span>
                                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: '#c084fc' }}>
                                                            ● Session / Daily
                                                        </span>
                                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: '#fbbf24' }}>
                                                            ● Macro HTF
                                                        </span>
                                                    </TimeframeLegend>
                                                </TimeframeSpectrumHeader>

                                                <TimeframeTrack>
                                                    {tfList.map((tf, idx) => {
                                                        const st = getTierStyle(tf.tier);
                                                        return (
                                                            <React.Fragment key={tf.label}>
                                                                <TimeframePill bg={st.bg} color={st.color} border={st.border} glow={st.glow}>
                                                                    <HighlightText text={tf.label} query={query} />
                                                                </TimeframePill>
                                                                {idx < tfList.length - 1 && <TimeframeArrow>→</TimeframeArrow>}
                                                            </React.Fragment>
                                                        );
                                                    })}
                                                </TimeframeTrack>

                                                {hasStepRule && (
                                                    <TimeframeStepRuleBox>
                                                        <span style={{ fontSize: '1rem', color: '#38bdf8' }}>⚡</span>
                                                        <div>
                                                            <strong style={{ color: '#38bdf8', marginRight: '6px' }}>Step Rule:</strong>
                                                            <span style={{ color: '#fbbf24', fontWeight: '600' }}><HighlightText text="Reference / Bias TF" query={query} /></span>
                                                            <span style={{ color: '#94a3b8', margin: '0 4px' }}>→</span>
                                                            <span style={{ color: '#c084fc', fontWeight: '600' }}><HighlightText text="Observation TF" query={query} /></span>
                                                            <span style={{ color: '#94a3b8', margin: '0 4px' }}>→</span>
                                                            <span style={{ color: '#38bdf8', fontWeight: '600' }}><HighlightText text="Execution TF" query={query} /></span>
                                                            <span style={{ marginLeft: '8px', color: '#94a3b8', fontSize: '0.82rem' }}>
                                                                (1–2 steps between each, <strong style={{ color: '#34d399' }}>2 steps as default</strong>)
                                                            </span>
                                                        </div>
                                                    </TimeframeStepRuleBox>
                                                )}
                                            </TimeframeSpectrumCard>
                                        );
                                    }

                                    if (block.includes('Reference / Bias TF → Observation TF → Execution TF')) {
                                        return (
                                            <TimeframeStepRuleBox key={bIdx} style={{ margin: '8px 0 12px 0' }}>
                                                <span style={{ fontSize: '1rem', color: '#38bdf8' }}>⚡</span>
                                                <div>
                                                    <strong style={{ color: '#38bdf8', marginRight: '6px' }}>Step Rule:</strong>
                                                    <span style={{ color: '#fbbf24', fontWeight: '600' }}><HighlightText text="Reference / Bias TF" query={query} /></span>
                                                    <span style={{ color: '#94a3b8', margin: '0 4px' }}>→</span>
                                                    <span style={{ color: '#c084fc', fontWeight: '600' }}><HighlightText text="Observation TF" query={query} /></span>
                                                    <span style={{ color: '#94a3b8', margin: '0 4px' }}>→</span>
                                                    <span style={{ color: '#38bdf8', fontWeight: '600' }}><HighlightText text="Execution TF" query={query} /></span>
                                                    <span style={{ marginLeft: '8px', color: '#94a3b8', fontSize: '0.82rem' }}>
                                                        (1–2 steps between each, <strong style={{ color: '#34d399' }}>2 steps as default</strong>)
                                                    </span>
                                                </div>
                                            </TimeframeStepRuleBox>
                                        );
                                    }

                                    if (/volatility\s+vs\.?\s+momentum/i.test(block)) {
                                        return (
                                            <VolatilityVsMomentumCard key={bIdx}>
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px', marginBottom: '12px', paddingBottom: '8px', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.84rem', fontWeight: '700', color: '#38bdf8', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                                                        <span>⚡</span>
                                                        <span>Volatility vs. Momentum</span>
                                                    </div>
                                                    <span style={{ fontSize: '0.72rem', padding: '2px 8px', borderRadius: '4px', background: 'rgba(56, 189, 248, 0.12)', color: '#38bdf8', border: '1px solid rgba(56, 189, 248, 0.3)', fontWeight: '700' }}>
                                                        CORE DISTINCTION
                                                    </span>
                                                </div>

                                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '10px', marginBottom: '12px' }}>
                                                    {/* Volatility */}
                                                    <div style={{ background: 'rgba(56, 189, 248, 0.05)', border: '1px solid rgba(56, 189, 248, 0.22)', borderRadius: '8px', padding: '12px 14px' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#38bdf8', fontWeight: '700', fontSize: '0.86rem', marginBottom: '6px' }}>
                                                            <span>●</span>
                                                            <span>Volatility / Amplitude</span>
                                                        </div>
                                                        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.82rem', color: '#f1f5f9', fontWeight: '600', marginBottom: '6px' }}>
                                                            Total Candle Range (High − Low)
                                                        </div>
                                                        <div style={{ fontSize: '0.82rem', color: '#94a3b8', lineHeight: '1.45' }}>
                                                            → Large range = high volatility / large price movement
                                                        </div>
                                                    </div>

                                                    {/* Momentum */}
                                                    <div style={{ background: 'rgba(168, 85, 247, 0.05)', border: '1px solid rgba(168, 85, 247, 0.22)', borderRadius: '8px', padding: '12px 14px' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#c084fc', fontWeight: '700', fontSize: '0.86rem', marginBottom: '6px' }}>
                                                            <span>●</span>
                                                            <span>Momentum</span>
                                                        </div>
                                                        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.82rem', color: '#f1f5f9', fontWeight: '600', marginBottom: '6px' }}>
                                                            Body Size + Close Position
                                                        </div>
                                                        <div style={{ fontSize: '0.82rem', color: '#94a3b8', lineHeight: '1.45' }}>
                                                            <div>→ Large body = strong directional pressure</div>
                                                            <div>→ Body large + close near high/low = stronger momentum</div>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Comparison Examples */}
                                                <div style={{ background: 'rgba(0, 0, 0, 0.25)', border: '1px solid rgba(255, 255, 255, 0.06)', borderRadius: '8px', padding: '10px 12px' }}>
                                                    <div style={{ fontSize: '0.78rem', fontWeight: '700', color: '#fbbf24', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                                                        💡 Comparison Examples (100-pt Candle):
                                                    </div>
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px', fontSize: '0.84rem', padding: '6px 10px', borderRadius: '6px', background: 'rgba(255, 255, 255, 0.02)' }}>
                                                            <span style={{ fontFamily: "'JetBrains Mono', monospace", color: '#e2e8f0' }}>
                                                                100-pt candle + 20-pt body
                                                            </span>
                                                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '3px 9px', borderRadius: '5px', background: 'rgba(245, 158, 11, 0.15)', color: '#fbbf24', border: '1px solid rgba(245, 158, 11, 0.35)', fontSize: '0.78rem', fontWeight: '700' }}>
                                                                High Volatility · Weak Momentum
                                                            </span>
                                                        </div>
                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px', fontSize: '0.84rem', padding: '6px 10px', borderRadius: '6px', background: 'rgba(255, 255, 255, 0.02)' }}>
                                                            <span style={{ fontFamily: "'JetBrains Mono', monospace", color: '#e2e8f0' }}>
                                                                100-pt candle + 85-pt body
                                                            </span>
                                                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '3px 9px', borderRadius: '5px', background: 'rgba(16, 185, 129, 0.15)', color: '#34d399', border: '1px solid rgba(16, 185, 129, 0.35)', fontSize: '0.78rem', fontWeight: '700' }}>
                                                                High Volatility · Strong Momentum
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </VolatilityVsMomentumCard>
                                        );
                                    }

                                    if (block.includes('HH + HL = Uptrend')) {
                                        return (
                                            <div key={bIdx} style={{ margin: '8px 0' }}>
                                                <DocFormulaPill>
                                                    <span>⚡</span>
                                                    <span>HH + HL = Uptrend</span>
                                                </DocFormulaPill>
                                            </div>
                                        );
                                    }

                                    // Key Principle handling (both structured confluence and single statement)
                                    if (/^\*{0,2}key\s+principle:?\*{0,2}/i.test(block.trim())) {
                                        const cleanBlock = block.replace(/\*\*/g, '');
                                        const hasKeyConfluence = cleanBlock.includes('Primary High/Low →') && cleanBlock.includes('Horizontal Level →');
                                        if (hasKeyConfluence) {
                                            const lines = block.split('\n').filter(l => l.includes('→'));
                                            if (lines.length >= 2) {
                                                const boxTitle = 'Key Principle & Structural Confluence';
                                                return (
                                                    <KeyPrincipleBox key={bIdx}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', color: '#fbbf24', fontWeight: '700', fontSize: '0.94rem' }}>
                                                            <span>⚡</span>
                                                            <span>{boxTitle}</span>
                                                        </div>
                                                        {lines.map((line, lIdx) => {
                                                            const cleanLine = line.replace(/^[-*]\s*/, '').replace(/^\*\*|\*\*$/g, '');
                                                            const parts = cleanLine.split('→');
                                                            const leftLabel = (parts[0] || '').replace(/\*\*/g, '').trim();
                                                            const rightDesc = (parts.slice(1).join('→') || '').replace(/\*\*/g, '').trim();
                                                            const isConfluence = lIdx === lines.length - 1 && lines.length > 2;
                                                            return (
                                                                <KeyPrincipleRow key={lIdx} isConfluence={isConfluence}>
                                                                    <span style={{
                                                                        padding: isConfluence ? '4px 10px' : '3px 8px',
                                                                        borderRadius: isConfluence ? '6px' : '5px',
                                                                        background: isConfluence ? 'linear-gradient(135deg, rgba(245, 158, 11, 0.35), rgba(217, 119, 6, 0.25))' : lIdx === 0 ? 'rgba(56, 189, 248, 0.16)' : 'rgba(168, 85, 247, 0.16)',
                                                                        color: isConfluence ? '#fbbf24' : lIdx === 0 ? '#38bdf8' : '#c084fc',
                                                                        fontWeight: '700',
                                                                        fontSize: '0.78rem',
                                                                        whiteSpace: 'nowrap',
                                                                        border: isConfluence ? '1px solid rgba(245, 158, 11, 0.45)' : 'none'
                                                                    }}>
                                                                        {leftLabel}
                                                                    </span>
                                                                    <span style={{ color: isConfluence ? '#fbbf24' : '#94a3b8' }}>→</span>
                                                                    <span style={{ fontWeight: isConfluence ? '700' : '500', color: isConfluence ? '#fef08a' : 'inherit' }}>
                                                                        {renderInlineMarkdown(rightDesc, query)}
                                                                    </span>
                                                                </KeyPrincipleRow>
                                                            );
                                                        })}
                                                    </KeyPrincipleBox>
                                                );
                                            }
                                        }

                                        const principleText = block
                                            .replace(/^\*{0,2}key\s+principle:?\*{0,2}\s*/i, '')
                                            .trim();
                                        if (principleText) {
                                            const principleLines = principleText.split('\n').map(l => l.trim()).filter(Boolean);
                                            const isList = principleLines.length > 1;

                                            return (
                                                <KeyPrincipleBox key={bIdx}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: isList ? '10px' : '8px', color: '#fbbf24', fontWeight: '700', fontSize: '0.94rem' }}>
                                                        <span>⚡</span>
                                                        <span>Key Principle</span>
                                                    </div>
                                                    {isList ? (
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                                            {principleLines.map((line, lIdx) => {
                                                                const cleanLine = line.replace(/^[-*]\s*/, '').trim();
                                                                return (
                                                                    <div key={lIdx} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '0.91rem', lineHeight: '1.5', color: '#fef08a' }}>
                                                                        <span style={{ color: '#fbbf24', marginTop: '2px', fontSize: '0.75rem' }}>◆</span>
                                                                        <div style={{ flex: 1 }}>{renderInlineMarkdown(cleanLine, query)}</div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    ) : (
                                                        <div style={{ fontSize: '0.92rem', lineHeight: '1.6', color: '#fef08a', fontWeight: '500' }}>
                                                            {renderInlineMarkdown(principleText, query)}
                                                        </div>
                                                    )}
                                                </KeyPrincipleBox>
                                            );
                                        }
                                        return null;
                                    }

                                    // Caution / Attention / Warning alert callout
                                    if (/^(?:>\s*)?(?:⚠️\s*)?\*{0,2}(?:caution|attention|warning):?\*{0,2}/i.test(block.trim()) || block.startsWith('> ⚠️') || block.startsWith('> Caution') || block.startsWith('> **Caution')) {
                                        const cleanCaution = block
                                            .replace(/^>\s*/, '')
                                            .replace(/^⚠️\s*/, '')
                                            .replace(/^\*{0,2}(?:caution|attention|warning):?\*{0,2}\s*/i, '')
                                            .trim();
                                        return (
                                            <InvalidationAlert key={bIdx} style={{
                                                background: 'rgba(245, 158, 11, 0.08)',
                                                border: '1px solid rgba(245, 158, 11, 0.35)',
                                                color: '#fbbf24',
                                                marginTop: '12px'
                                            }}>
                                                <span style={{ fontSize: '1.1rem' }}>⚠️</span>
                                                <div>
                                                    <strong style={{ color: '#fef08a' }}>Caution:</strong> {renderInlineMarkdown(cleanCaution, query)}
                                                </div>
                                            </InvalidationAlert>
                                        );
                                    }

                                    if (!/^\s*\d+\.\s+/.test(block) && (block.includes('**Primary Low**') || /^\s*[-*]?\s*Primary Low:/i.test(block))) {
                                        const cleanLowText = block
                                            .replace(/^[-*]?\s*\*\*Primary Low\*\*:\s*/i, '')
                                            .replace(/^[-*]?\s*Primary Low:?\s*/i, '')
                                            .trim();
                                        const hasInvalidation = cleanLowText.includes('invalidated') || cleanLowText.includes('Structural Shift');
                                        let mainLowDesc = cleanLowText;
                                        let invalidationSentence = "";
                                        if (hasInvalidation) {
                                            const lines = cleanLowText.split('\n');
                                            const invLines = [];
                                            const descLines = [];
                                            lines.forEach(l => {
                                                if (l.includes('invalidated') || l.includes('Structural Shift')) {
                                                    invLines.push(l);
                                                } else {
                                                    descLines.push(l);
                                                }
                                            });
                                            invalidationSentence = invLines.join(' ').trim();
                                            mainLowDesc = descLines.join(' ').trim();
                                        }

                                        return (
                                            <DocPivotCard key={bIdx} bg="rgba(16, 185, 129, 0.05)" border="rgba(16, 185, 129, 0.25)" accent="#10b981">
                                                <DocPivotHeader>
                                                    <DocPivotBadge bg="rgba(16, 185, 129, 0.2)" color="#34d399" border="rgba(16, 185, 129, 0.4)">
                                                        ● Primary Low (Key Floor)
                                                    </DocPivotBadge>
                                                </DocPivotHeader>
                                                <DocParagraph style={{ marginBottom: invalidationSentence ? '6px' : '0' }}>
                                                    {renderInlineMarkdown(mainLowDesc || cleanLowText, query)}
                                                </DocParagraph>
                                                {invalidationSentence && (
                                                    <InvalidationAlert>
                                                        <span style={{ fontSize: '1rem' }}>⚠️</span>
                                                        <div>
                                                             {renderInlineMarkdown(invalidationSentence, query)}
                                                        </div>
                                                    </InvalidationAlert>
                                                )}
                                            </DocPivotCard>
                                        );
                                    }

                                    if (!/^\s*\d+\.\s+/.test(block) && (block.includes('**Primary High**') || /^\s*[-*]?\s*Primary High:/i.test(block))) {
                                        const cleanHighText = block
                                            .replace(/^[-*]?\s*\*\*Primary High\*\*:\s*/i, '')
                                            .replace(/^[-*]?\s*Primary High:?\s*/i, '')
                                            .trim();
                                        return (
                                            <DocPivotCard key={bIdx} bg="rgba(56, 189, 248, 0.05)" border="rgba(56, 189, 248, 0.25)" accent="#0ea5e9">
                                                <DocPivotHeader>
                                                    <DocPivotBadge bg="rgba(56, 189, 248, 0.2)" color="#38bdf8" border="rgba(56, 189, 248, 0.4)">
                                                        ● Primary High (Expansion Peak)
                                                    </DocPivotBadge>
                                                </DocPivotHeader>
                                                <DocParagraph style={{ margin: 0 }}>
                                                    {renderInlineMarkdown(cleanHighText, query)}
                                                </DocParagraph>
                                            </DocPivotCard>
                                        );
                                    }

                                    if (!/^\s*\d+\.\s+/.test(block) && (/^\s*[-*]?\s*\*\*Upside Stops/i.test(block) || /^\s*[-*]?\s*Upside Stops:/i.test(block))) {
                                        const cleanBslText = block
                                            .replace(/^[-*]?\s*\*\*Upside Stops(?:\s*\([^)]*\))?\*\*:\s*/i, '')
                                            .replace(/^[-*]?\s*Upside Stops(?:\s*\([^)]*\))?:?\s*/i, '')
                                            .trim();
                                        return (
                                            <DocPivotCard key={bIdx} bg="rgba(56, 189, 248, 0.05)" border="rgba(56, 189, 248, 0.25)" accent="#0ea5e9">
                                                <DocPivotHeader>
                                                    <DocPivotBadge bg="rgba(56, 189, 248, 0.2)" color="#38bdf8" border="rgba(56, 189, 248, 0.4)">
                                                        ● Upside Stops (Buy Stop Pool)
                                                    </DocPivotBadge>
                                                </DocPivotHeader>
                                                <DocParagraph style={{ margin: 0 }}>
                                                    {renderInlineMarkdown(cleanBslText, query)}
                                                </DocParagraph>
                                            </DocPivotCard>
                                        );
                                    }

                                    if (!/^\s*\d+\.\s+/.test(block) && (/^\s*[-*]?\s*\*\*Downside Stops/i.test(block) || /^\s*[-*]?\s*Downside Stops:/i.test(block))) {
                                        const cleanSslText = block
                                            .replace(/^[-*]?\s*\*\*Downside Stops(?:\s*\([^)]*\))?\*\*:\s*/i, '')
                                            .replace(/^[-*]?\s*Downside Stops(?:\s*\([^)]*\))?:?\s*/i, '')
                                            .trim();
                                        return (
                                            <DocPivotCard key={bIdx} bg="rgba(16, 185, 129, 0.05)" border="rgba(16, 185, 129, 0.25)" accent="#10b981">
                                                <DocPivotHeader>
                                                    <DocPivotBadge bg="rgba(16, 185, 129, 0.2)" color="#34d399" border="rgba(16, 185, 129, 0.4)">
                                                        ● Downside Stops (Sell Stop Pool)
                                                    </DocPivotBadge>
                                                </DocPivotHeader>
                                                <DocParagraph style={{ margin: 0 }}>
                                                    {renderInlineMarkdown(cleanSslText, query)}
                                                </DocParagraph>
                                            </DocPivotCard>
                                        );
                                    }

                                    if (block.includes('Core Axiom') || /^\s*[-*]?\s*Core Axiom/i.test(block)) {
                                        let cleanAxiomText = block
                                            .replace(/^[>\s⚡*-]+/g, '')
                                            .replace(/^\*\*Core Axiom(?:\*\*)?:?\s*/i, '')
                                            .replace(/^Core Axiom:?\s*/i, '')
                                            .replace(/^[>\s⚡*-]+/g, '')
                                            .replace(/^\*\*Core Axiom(?:\*\*)?:?\s*/i, '')
                                            .replace(/^Core Axiom:?\s*/i, '')
                                            .replace(/\*([“"][^*]+[”"])\*/g, '$1')
                                            .replace(/^\*+|\*+$/g, '')
                                            .trim();
                                        return (
                                            <DocPivotCard key={bIdx} bg="rgba(245, 158, 11, 0.06)" border="rgba(245, 158, 11, 0.3)" accent="#f59e0b">
                                                <DocPivotHeader>
                                                    <DocPivotBadge bg="rgba(245, 158, 11, 0.2)" color="#fbbf24" border="rgba(245, 158, 11, 0.4)">
                                                        ⚡ Core Axiom
                                                    </DocPivotBadge>
                                                </DocPivotHeader>
                                                <DocParagraph style={{ margin: 0, fontSize: '0.94rem', lineHeight: '1.65', fontStyle: 'italic', color: '#fef08a' }}>
                                                    {renderInlineMarkdown(cleanAxiomText, query)}
                                                </DocParagraph>
                                            </DocPivotCard>
                                        );
                                    }

                                    if (/^\s*⚡\s*\*\*/.test(block.trim())) {
                                        const cleanText = block.trim().replace(/^\s*⚡\s*/, '');
                                        return (
                                            <DocPivotCard key={bIdx} bg="rgba(56, 189, 248, 0.05)" border="rgba(56, 189, 248, 0.25)" accent="#38bdf8">
                                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                                                    <span style={{ fontSize: '1.1rem', color: '#38bdf8', marginTop: '1px' }}>⚡</span>
                                                    <div style={{ flex: 1, fontSize: '0.92rem', lineHeight: '1.6', color: '#e2e8f0' }}>
                                                        {renderInlineMarkdown(cleanText, query)}
                                                    </div>
                                                </div>
                                            </DocPivotCard>
                                        );
                                    }

                                    // Decision Gate block with custom color theme per gate type
                                    if (!block.startsWith('#') && !/^\s*\d+\.\s+/.test(block) && (
                                        /(?:\*\*|\b)(?:[^*:\n\r]*(?:Gate|⛩\uFE0F?))(?:\*\*|\b)/i.test(block) ||
                                        /^\s*[-*]?\s*(?:\*\*)?(?:Gate|⛩\uFE0F?)(?::?\*\*|\*\*:?|:)?\s*$/im.test(block) ||
                                        block.includes('Decision Gate')
                                    )) {
                                        const titleMatch = block.match(/\*\*([^*]+)\*\*/);
                                        let ruleTitle = titleMatch ? titleMatch[1].trim().replace(/:$/, '') : '⛩️';
                                        if (ruleTitle.toLowerCase().includes('gate') || /⛩\uFE0F?/.test(ruleTitle)) {
                                            ruleTitle = '⛩️';
                                        }

                                        const lower = ((cleanTitle || '') + ' ' + (ruleTitle || '') + ' ' + block.slice(0, 160)).toLowerCase();
                                        let gateTheme = {
                                            badgeIcon: '⚡',
                                            cardBg: 'rgba(245, 158, 11, 0.08)',
                                            cardBorder: 'rgba(245, 158, 11, 0.35)',
                                            cardAccent: '#f59e0b',
                                            badgeBg: 'rgba(245, 158, 11, 0.22)',
                                            badgeColor: '#fbbf24',
                                            badgeBorder: 'rgba(245, 158, 11, 0.45)',
                                            subBorder: '2px solid rgba(245, 158, 11, 0.4)',
                                            bulletColor: '#fbbf24',
                                            textColor: '#fef08a'
                                        };

                                        if (lower.includes('find and choose') || lower.includes('choose assets') || lower.includes('asset selection') || lower.includes('podium') || lower.includes('score ≥50') || lower.includes('score >=50') || lower.includes('macro mood') || lower.includes('discovery gate') || lower.includes('exploration gate') || lower.includes('selection gate')) {
                                            // Gate 1: Cyan / Sky Blue
                                            gateTheme = {
                                                badgeIcon: '🧭',
                                                cardBg: 'rgba(14, 165, 233, 0.08)',
                                                cardBorder: 'rgba(14, 165, 233, 0.35)',
                                                cardAccent: '#0ea5e9',
                                                badgeBg: 'rgba(14, 165, 233, 0.2)',
                                                badgeColor: '#38bdf8',
                                                badgeBorder: 'rgba(14, 165, 233, 0.45)',
                                                subBorder: '2px solid rgba(14, 165, 233, 0.4)',
                                                bulletColor: '#38bdf8',
                                                textColor: '#bae6fd'
                                            };
                                        } else if (lower.includes('quick elimination') || lower.includes('filter decision') || lower.includes('seasonality') || lower.includes('rotation') || lower.includes('fuel oppose') || lower.includes('etf money') || lower.includes('flow & positioning') || lower.includes('flow gate') || lower.includes('positioning gate') || lower.includes('elimination gate')) {
                                            // Gate 2: Amber / Gold
                                            gateTheme = {
                                                badgeIcon: '⚡',
                                                cardBg: 'rgba(245, 158, 11, 0.08)',
                                                cardBorder: 'rgba(245, 158, 11, 0.35)',
                                                cardAccent: '#f59e0b',
                                                badgeBg: 'rgba(245, 158, 11, 0.22)',
                                                badgeColor: '#fbbf24',
                                                badgeBorder: 'rgba(245, 158, 11, 0.45)',
                                                subBorder: '2px solid rgba(245, 158, 11, 0.4)',
                                                bulletColor: '#fbbf24',
                                                textColor: '#fef08a'
                                            };
                                        } else if (lower.includes('catalysts and core') || lower.includes('core drivers') || lower.includes('core economy') || lower.includes('currency strength') || lower.includes('retail noise') || lower.includes('fundamental') || lower.includes('driver gate') || lower.includes('bedrock gate') || lower.includes('catalyst gate')) {
                                            // Gate 3: Purple / Violet
                                            gateTheme = {
                                                badgeIcon: '🏛️',
                                                cardBg: 'rgba(168, 85, 247, 0.08)',
                                                cardBorder: 'rgba(168, 85, 247, 0.35)',
                                                cardAccent: '#a855f7',
                                                badgeBg: 'rgba(168, 85, 247, 0.2)',
                                                badgeColor: '#c084fc',
                                                badgeBorder: 'rgba(168, 85, 247, 0.45)',
                                                subBorder: '2px solid rgba(168, 85, 247, 0.4)',
                                                bulletColor: '#c084fc',
                                                textColor: '#e9d5ff'
                                            };
                                        } else if (lower.includes('pre-entry') || lower.includes('execution') || lower.includes('15–30 minutes') || lower.includes('15-30 minutes') || lower.includes('stop/exit') || lower.includes('all 4 gates') || lower.includes('risk gate')) {
                                            // Gate 4: Emerald / Green
                                            gateTheme = {
                                                badgeIcon: '🎯',
                                                cardBg: 'rgba(16, 185, 129, 0.08)',
                                                cardBorder: 'rgba(16, 185, 129, 0.35)',
                                                cardAccent: '#10b981',
                                                badgeBg: 'rgba(16, 185, 129, 0.2)',
                                                badgeColor: '#34d399',
                                                badgeBorder: 'rgba(16, 185, 129, 0.45)',
                                                subBorder: '2px solid rgba(16, 185, 129, 0.4)',
                                                bulletColor: '#34d399',
                                                textColor: '#a7f3d0'
                                            };
                                        }

                                        let cleanRuleText = block.trim();
                                        if (cleanRuleText.startsWith('>')) {
                                            cleanRuleText = cleanRuleText.split('\n').map(l => l.replace(/^>\s?/, '')).join('\n').trim();
                                        }
                                        // Strip gate header line
                                        cleanRuleText = cleanRuleText.replace(/^\s*[-*]?\s*(?:\*\*)?(?:[^*:\n\r]*(?:Gate|⛩\uFE0F?))(?::?\*\*|\*\*:?|:)?\s*(\n|$)/i, '');
                                        cleanRuleText = cleanRuleText.trim();

                                        const ruleLines = cleanRuleText.split('\n').map(l => l.trim()).filter(Boolean);
                                        let mainRuleText = '';
                                        let subRuleLines = [];
                                        if (ruleLines.length === 1 && !/^[-*]\s/.test(ruleLines[0])) {
                                            mainRuleText = ruleLines[0];
                                        } else if (ruleLines.length > 1 && !/^[-*]\s/.test(ruleLines[0]) && /^[-*]\s/.test(ruleLines[1])) {
                                            mainRuleText = ruleLines[0];
                                            subRuleLines = ruleLines.slice(1);
                                        } else {
                                            subRuleLines = ruleLines;
                                        }

                                        return (
                                            <DocPivotCard key={bIdx} bg={gateTheme.cardBg} border={gateTheme.cardBorder} accent={gateTheme.cardAccent}>
                                                <DocPivotHeader>
                                                    <DocPivotBadge bg={gateTheme.badgeBg} color={gateTheme.badgeColor} border={gateTheme.badgeBorder}>
                                                        {ruleTitle}
                                                    </DocPivotBadge>
                                                </DocPivotHeader>
                                                {mainRuleText && (
                                                    <DocParagraph style={{ margin: 0, fontSize: '0.94rem', lineHeight: '1.6', fontWeight: '600', color: gateTheme.textColor }}>
                                                        {renderInlineMarkdown(mainRuleText, query)}
                                                    </DocParagraph>
                                                )}
                                                {subRuleLines.length > 0 && (
                                                    <div style={{
                                                        marginTop: mainRuleText ? '10px' : '2px',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        gap: '8px',
                                                        paddingLeft: '14px',
                                                        borderLeft: gateTheme.subBorder
                                                    }}>
                                                        {subRuleLines.map((sub, sIdx) => {
                                                            const cleanSub = sub.replace(/^[-*]\s*/, '');
                                                            return (
                                                                <div key={sIdx} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '0.89rem', lineHeight: '1.55', color: '#cbd5e1' }}>
                                                                    <span style={{ color: gateTheme.bulletColor, marginTop: '3px', fontSize: '0.72rem', flexShrink: 0 }}>◆</span>
                                                                    <div style={{ flex: 1 }}>
                                                                        {renderInlineMarkdown(cleanSub, query)}
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </DocPivotCard>
                                        );
                                    }

                                    // Temporal Consistency & Multi-TF Rule block
                                    if (!block.startsWith('#') && !/^\s*\d+\.\s+/.test(block) && (
                                        block.includes('Temporal Consistency') ||
                                        block.includes('Temporal Symmetry') ||
                                        block.includes('Multi-TF Rule') ||
                                        block.includes('Multi-Timeframe Rule') ||
                                        /^\s*[-*]?\s*Duration Consistency Rule:/i.test(block)
                                    )) {
                                        const titleMatch = block.match(/\*\*([^*]+)\*\*/);
                                        const isMultiTf = block.toLowerCase().includes('multi-tf') || block.toLowerCase().includes('timeframe');
                                        const ruleTitle = titleMatch ? titleMatch[1].trim().replace(/:$/, '') : (isMultiTf ? 'Multi-TF Rule' : 'Temporal Consistency');
                                        const badgeIcon = isMultiTf ? '🪜' : '⏱️';
                                        const cardBg = 'rgba(168, 85, 247, 0.05)';
                                        const cardBorder = 'rgba(168, 85, 247, 0.25)';
                                        const cardAccent = '#a855f7';
                                        const badgeBg = 'rgba(168, 85, 247, 0.2)';
                                        const badgeColor = '#c084fc';
                                        const badgeBorder = 'rgba(168, 85, 247, 0.4)';
                                        const subBorder = '2px solid rgba(168, 85, 247, 0.35)';

                                        const cleanRuleText = block
                                            .replace(/^[-*]?\s*\*\*[^*]+\*\*:\s*/i, '')
                                            .replace(/^[-*]?\s*\*\*[^*]+\*\*\s*/i, '')
                                            .replace(/^[-*]?\s*(?:Temporal|Multi-TF|Multi-Timeframe)[^:]*:?\s*/i, '')
                                            .trim();
                                        const ruleLines = cleanRuleText.split('\n').map(l => l.trim()).filter(Boolean);
                                        let mainRuleText = '';
                                        let subRuleLines = [];
                                        if (ruleLines.length > 0 && !/^[-*]\s/.test(ruleLines[0])) {
                                            mainRuleText = ruleLines[0];
                                            subRuleLines = ruleLines.slice(1);
                                        } else {
                                            subRuleLines = ruleLines;
                                        }

                                        return (
                                            <DocPivotCard key={bIdx} bg={cardBg} border={cardBorder} accent={cardAccent}>
                                                <DocPivotHeader>
                                                    <DocPivotBadge bg={badgeBg} color={badgeColor} border={badgeBorder}>
                                                        {badgeIcon} {ruleTitle}
                                                    </DocPivotBadge>
                                                </DocPivotHeader>
                                                {mainRuleText && (
                                                    <DocParagraph style={{ margin: 0 }}>
                                                        {renderInlineMarkdown(mainRuleText, query)}
                                                    </DocParagraph>
                                                )}
                                                {subRuleLines.length > 0 && (
                                                    <div style={{
                                                        marginTop: mainRuleText ? '10px' : '2px',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        gap: '6px',
                                                        paddingLeft: '12px',
                                                        borderLeft: subBorder
                                                    }}>
                                                        {subRuleLines.map((sub, sIdx) => {
                                                            const cleanSub = sub.replace(/^[-*]\s*/, '');
                                                            return (
                                                                <div key={sIdx} style={{ fontSize: '0.88rem', lineHeight: '1.55', color: '#cbd5e1' }}>
                                                                    {renderInlineMarkdown(cleanSub, query)}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </DocPivotCard>
                                        );
                                    }

                                    if (block.startsWith('#### ')) {
                                        const rawSubTitle = block.replace(/^####\s+/, '').trim();
                                        const subMatch = rawSubTitle.match(/^((?:\d+\.\d+|\d+|[A-Z]|Case\s+[A-Z]))[.:\s]+(.*)/i);
                                        const subBadge = subMatch ? subMatch[1] : '';
                                        const subText = subMatch ? subMatch[2] : rawSubTitle;
                                        return (
                                            <DocSubSectionHeader key={bIdx}>
                                                {subBadge && <DocSubSectionBadge>{subBadge}</DocSubSectionBadge>}
                                                <DocSubSectionTitle>{renderInlineMarkdown(formatTitleWithSubtitle(subText), query)}</DocSubSectionTitle>
                                            </DocSubSectionHeader>
                                        );
                                    }

                                    const imgMatches = [...block.matchAll(/!\[(.*?)\]\((.*?)\)/g)];
                                    if (imgMatches.length > 0) {
                                        const images = imgMatches.map(m => ({ caption: m[1], url: m[2] }));
                                        return (
                                            <DocInlineIllustrationCarousel
                                                key={bIdx}
                                                images={images}
                                                query={query}
                                                onOpenLightbox={onOpenLightbox}
                                            />
                                        );
                                    }

                                    const trimmedBlock = block.trim();
                                    if (/^(?:---+|\*\*\*+|___+|—{3,})$/.test(trimmedBlock)) {
                                        if (bIdx === blocks.length - 1) return null;
                                        return (
                                            <div
                                                key={bIdx}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '12px',
                                                    margin: '16px 0 14px 0',
                                                    padding: '2px 0'
                                                }}
                                            >
                                                <div style={{ flex: 1, height: '1px', background: 'linear-gradient(90deg, rgba(56, 189, 248, 0.3) 0%, rgba(255, 255, 255, 0.03) 100%)' }} />
                                                <span style={{ color: '#38bdf8', fontSize: '0.74rem', letterSpacing: '3px', opacity: 0.8, userSelect: 'none' }}>◈ ◈ ◈</span>
                                                <div style={{ flex: 1, height: '1px', background: 'linear-gradient(90deg, rgba(255, 255, 255, 0.03) 0%, rgba(56, 189, 248, 0.3) 100%)' }} />
                                            </div>
                                        );
                                    }

                                    if (trimmedBlock.startsWith('```')) {
                                        const codeContent = trimmedBlock.replace(/^```[a-zA-Z0-9_-]*\n?/, '').replace(/\n?```$/, '').trim();
                                        return (
                                            <DiagramContainer key={bIdx} style={{ margin: '12px 0 16px 0' }}>
                                                <pre>{codeContent}</pre>
                                            </DiagramContainer>
                                        );
                                    }

                                    if (trimmedBlock.startsWith('|') && trimmedBlock.includes('---')) {
                                        const lines = trimmedBlock.split('\n').map(l => l.trim()).filter(Boolean);
                                        if (lines.length >= 2 && lines[0].startsWith('|') && lines[1].includes('---')) {
                                            const parseRow = (line) => line.split(/(?<!\\)\|/).slice(1, -1).map(c => c.replace(/\\\|/g, '|').trim());
                                            const headers = parseRow(lines[0]);
                                            const rows = lines.slice(2).map(parseRow);
                                            return (
                                                <DocTableWrapper key={bIdx}>
                                                    <DocTable>
                                                        <thead>
                                                            <tr>
                                                                {headers.map((h, hIdx) => (
                                                                    <DocTh key={hIdx}>{renderInlineMarkdown(h, query)}</DocTh>
                                                                ))}
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {rows.map((row, rIdx) => (
                                                                <DocTr key={rIdx}>
                                                                    {row.map((cell, cIdx) => (
                                                                        <DocTd key={cIdx}>{renderInlineMarkdown(cell, query)}</DocTd>
                                                                    ))}
                                                                </DocTr>
                                                            ))}
                                                        </tbody>
                                                    </DocTable>
                                                </DocTableWrapper>
                                            );
                                        }
                                    }

                                    if (block.includes('Bold** = Required') || block.includes('Format Legend')) {
                                        return (
                                            <div
                                                key={bIdx}
                                                style={{
                                                    marginTop: '14px',
                                                    marginBottom: '10px',
                                                    padding: '8px 14px',
                                                    borderRadius: '6px',
                                                    background: 'rgba(255, 255, 255, 0.03)',
                                                    border: '1px solid rgba(255, 255, 255, 0.08)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '14px',
                                                    fontSize: '0.82rem',
                                                    flexWrap: 'wrap'
                                                }}
                                            >
                                                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                                    <span style={{ color: '#38bdf8', fontSize: '0.82rem' }}>●</span>
                                                    <strong style={{ color: '#38bdf8' }}>Bold</strong>
                                                    <span style={{ color: '#94a3b8' }}>=</span>
                                                    <span style={{ color: '#34d399', fontWeight: '700' }}>Required</span>
                                                </div>
                                                <span style={{ color: 'rgba(255, 255, 255, 0.2)' }}>|</span>
                                                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                                    <span style={{ color: '#94a3b8', fontSize: '0.82rem' }}>○</span>
                                                    <em style={{ color: '#94a3b8', fontStyle: 'italic' }}>Italics</em>
                                                    <span style={{ color: '#94a3b8' }}>=</span>
                                                    <span style={{ color: '#cbd5e1', fontWeight: '600' }}>Optional</span>
                                                </div>
                                            </div>
                                        );
                                    }

                                    if (/^\s*\d+\.\s+/.test(block)) {
                                        const rawLines = block.split('\n').filter(l => l.trim().length > 0);
                                        const items = [];
                                        rawLines.forEach(line => {
                                            const match = line.match(/^\s*(\d+)\.\s+([\s\S]*)/);
                                            const isBullet = /^\s*[-*]\s/.test(line);
                                            const isTableLine = /^\s*\|.*\|\s*$/.test(line);
                                            if (match) {
                                                items.push({ num: match[1], text: match[2].trim(), subItems: [] });
                                            } else if (isTableLine && items.length > 0) {
                                                const lastSub = items[items.length - 1].subItems[items[items.length - 1].subItems.length - 1];
                                                if (lastSub && lastSub.type === 'table') {
                                                    lastSub.lines.push(line.trim());
                                                } else {
                                                    items[items.length - 1].subItems.push({ type: 'table', lines: [line.trim()] });
                                                }
                                            } else if (isBullet && items.length > 0) {
                                                const leadingSpaces = (line.match(/^(\s*)/) || ['', ''])[1].length;
                                                const text = line.replace(/^\s*[-*]\s+/, '').trim();
                                                items[items.length - 1].subItems.push({ text, leadingSpaces });
                                            } else if (items.length > 0) {
                                                items[items.length - 1].text += ' ' + line.trim();
                                            }
                                        });
                                        return (
                                            <div key={bIdx} style={{ display: 'flex', flexDirection: 'column', gap: '10px', margin: '10px 0 14px 0', width: '100%' }}>
                                                {items.map((it, itIdx) => {
                                                    const nonTableSubs = (it.subItems || []).filter(s => s && s.type !== 'table');
                                                    const minIndent = nonTableSubs.length > 0
                                                        ? Math.min(...nonTableSubs.map(s => typeof s === 'object' ? s.leadingSpaces : 0))
                                                        : 0;

                                                    return (
                                                        <div
                                                            key={itIdx}
                                                            style={{
                                                                display: 'flex',
                                                                alignItems: 'flex-start',
                                                                gap: '12px',
                                                                background: 'rgba(255, 255, 255, 0.02)',
                                                                border: '1px solid rgba(255, 255, 255, 0.06)',
                                                                borderRadius: '8px',
                                                                padding: '10px 14px'
                                                            }}
                                                        >
                                                            <span style={{
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                minWidth: '24px',
                                                                height: '24px',
                                                                borderRadius: '6px',
                                                                background: 'linear-gradient(135deg, rgba(56, 189, 248, 0.2), rgba(14, 165, 233, 0.2))',
                                                                border: '1px solid rgba(56, 189, 248, 0.4)',
                                                                color: '#38bdf8',
                                                                fontSize: '0.8rem',
                                                                fontWeight: '700',
                                                                flexShrink: 0,
                                                                marginTop: '2px'
                                                            }}>
                                                                {it.num}
                                                            </span>
                                                            <div style={{ flex: 1, fontSize: '0.91rem', lineHeight: '1.6', color: '#cbd5e1' }}>
                                                                {renderInlineMarkdown(it.text, query)}
                                                                {it.subItems && it.subItems.length > 0 && (
                                                                    <div style={{
                                                                        marginTop: '10px',
                                                                        marginLeft: '2px',
                                                                        paddingLeft: '12px',
                                                                        borderLeft: '2px solid rgba(56, 189, 248, 0.22)',
                                                                        display: 'flex',
                                                                        flexDirection: 'column',
                                                                        gap: '6px'
                                                                    }}>
                                                                        {it.subItems.map((subItem, sIdx) => {
                                                                            if (subItem && subItem.type === 'table') {
                                                                                const tableLines = subItem.lines;
                                                                                if (tableLines.length >= 2 && tableLines[1].includes('---')) {
                                                                                    const parseRow = (l) => l.split(/(?<!\\)\|/).slice(1, -1).map(c => c.replace(/\\\|/g, '|').trim());
                                                                                    const headers = parseRow(tableLines[0]);
                                                                                    const rows = tableLines.slice(2).map(parseRow);
                                                                                    return (
                                                                                        <DocTableWrapper key={sIdx} style={{ margin: '8px 0', width: '100%' }}>
                                                                                            <DocTable>
                                                                                                <thead>
                                                                                                    <tr>
                                                                                                        {headers.map((h, hIdx) => (
                                                                                                            <DocTh key={hIdx}>{renderInlineMarkdown(h, query)}</DocTh>
                                                                                                        ))}
                                                                                                    </tr>
                                                                                                </thead>
                                                                                                <tbody>
                                                                                                    {rows.map((row, rIdx) => (
                                                                                                        <DocTr key={rIdx}>
                                                                                                            {row.map((cell, cIdx) => (
                                                                                                                <DocTd key={cIdx}>{renderInlineMarkdown(cell, query)}</DocTd>
                                                                                                            ))}
                                                                                                        </DocTr>
                                                                                                    ))}
                                                                                                </tbody>
                                                                                            </DocTable>
                                                                                        </DocTableWrapper>
                                                                                    );
                                                                                }
                                                                                return null;
                                                                            }

                                                                            const sub = typeof subItem === 'string' ? subItem : subItem.text;
                                                                            const spaces = typeof subItem === 'object' ? subItem.leadingSpaces : minIndent;
                                                                            const diff = spaces - minIndent;
                                                                            const level = diff <= 0 ? 0 : diff <= 2 ? 1 : 2;
                                                                            const isLightning = /^\s*⚡/.test(sub);
                                                                            const isMainHeader = level === 0 && (/^\*\*Route\s+[AB]/i.test(sub) || /^\*\*Goal/i.test(sub) || /^\*\*[A-Z]\./i.test(sub));

                                                                            return (
                                                                                <div
                                                                                    key={sIdx}
                                                                                    style={{
                                                                                        display: 'flex',
                                                                                        alignItems: 'flex-start',
                                                                                        gap: level === 0 ? '8px' : '6px',
                                                                                        fontSize: level === 2 ? '0.84rem' : level === 1 ? '0.88rem' : '0.90rem',
                                                                                        lineHeight: '1.55',
                                                                                        color: isLightning ? 'rgba(125, 211, 252, 0.95)' : level === 2 ? '#94a3b8' : level === 1 ? '#cbd5e1' : '#f1f5f9',
                                                                                        fontWeight: isLightning ? 550 : isMainHeader ? 600 : 400,
                                                                                        padding: '2px 0',
                                                                                        paddingLeft: level === 2 ? '28px' : level === 1 ? '16px' : '0px',
                                                                                        marginTop: (sIdx > 0 && level === 0) ? '6px' : '0px'
                                                                                    }}
                                                                                >
                                                                                    {!isLightning && (
                                                                                        <span
                                                                                            style={{
                                                                                                color: level === 0 ? '#38bdf8' : level === 1 ? 'rgba(56, 189, 248, 0.65)' : 'rgba(192, 132, 252, 0.65)',
                                                                                                fontSize: level === 0 ? '0.95rem' : level === 1 ? '0.55rem' : '0.48rem',
                                                                                                fontWeight: 700,
                                                                                                lineHeight: level === 0 ? 1 : '1.8',
                                                                                                marginTop: level === 0 ? '2px' : '2px',
                                                                                                flexShrink: 0
                                                                                            }}
                                                                                        >
                                                                                            {level === 0 ? '›' : level === 1 ? '○' : '▪'}
                                                                                        </span>
                                                                                    )}
                                                                                    <span style={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px' }}>
                                                                                        {renderInlineMarkdown(sub, query)}
                                                                                    </span>
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        );
                                    }

                                    if (block.startsWith('- ') || block.startsWith('* ')) {
                                        const rawLines = block.split('\n').filter(l => l.trim().length > 0);
                                        const items = [];
                                        rawLines.forEach(line => {
                                            const isDashBullet = /^\s*[-*]\s/.test(line);
                                            const numMatch = line.match(/^(\s*)(\d+)\.\s+(.*)/);
                                            const isNumberedSub = !!numMatch;
                                            const isBullet = isDashBullet || isNumberedSub;
                                            if (isBullet) {
                                                const isSubSub = /^\s{4,}/.test(line) || /^\t{2,}/.test(line);
                                                const isSub = isNumberedSub || (!isSubSub && (/^\s{2,}/.test(line) || /^\t+/.test(line)));
                                                let text = '';
                                                if (isDashBullet) {
                                                    text = line.replace(/^\s*[-*]\s+/, '').trim();
                                                } else if (isNumberedSub) {
                                                    text = `${numMatch[2]}. ${numMatch[3].trim()}`;
                                                }
                                                const isNumbered = (isSub || isSubSub) && (isNumberedSub || /^\s*(?:\*\*)?\d+\./.test(text));
                                                const isChecklist = /^\s*\[([ xX])?\]\s*/.test(text);
                                                items.push({ text, isSub, isSubSub, isNumbered, isChecklist });
                                            } else if (items.length > 0) {
                                                items[items.length - 1].text += ' ' + line.trim();
                                            }
                                        });
                                        return (
                                            <DocBulletList key={bIdx}>
                                                {items.map((it, itIdx) => {
                                                    const isMicro = /^\s*[-*]?\s*\*\*Interpretation\*\*/i.test(it.text);
                                                    const isSeparatorIcon = (it.isSub || it.isSubSub) && /^\s*(?:⚡|◈|✦|❖|▸|›|▪|◆)\s*$/.test(it.text);
                                                    const isActionItem = !isSeparatorIcon && (/^\s*⚡/.test(it.text) || /^\s*🎯/.test(it.text));
                                                    const isMetaItem = it.isSub && /^\s*Assets\s*:/i.test(it.text);
                                                    const isChartBasedCard = cleanTitle.toLowerCase().includes('chart-based');
                                                    const isOptional = isLevelClassificationDoc && isChartBasedCard && !it.isSub && !it.isSubSub && /^\s*\*([^*]+)\*/.test(it.text) && !it.text.startsWith('**');
                                                    const isRequired = isLevelClassificationDoc && isChartBasedCard && !it.isSub && !it.isSubSub && /^\s*\*\*([^*]+)\*\*/.test(it.text) && !it.text.startsWith('***');

                                                    if (isSeparatorIcon) {
                                                        const iconChar = it.text.trim();
                                                        return (
                                                            <li
                                                                key={itIdx}
                                                                style={{
                                                                    listStyle: 'none',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: '8px',
                                                                    margin: '6px 0 4px 18px',
                                                                    padding: 0
                                                                }}
                                                            >
                                                                <span style={{
                                                                    fontSize: '0.78rem',
                                                                    color: 'rgba(125, 211, 252, 0.75)',
                                                                    display: 'inline-flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center'
                                                                }}>
                                                                    {iconChar}
                                                                </span>
                                                                <span style={{
                                                                    flex: 1,
                                                                    height: '1px',
                                                                    background: 'linear-gradient(90deg, rgba(56, 189, 248, 0.18) 0%, rgba(255, 255, 255, 0.02) 60%, transparent 100%)'
                                                                }} />
                                                            </li>
                                                        );
                                                    }

                                                    if (isMetaItem) {
                                                        const m = it.text.match(/^\s*(Assets\s*:)\s*(.*?)(?:\s*(?:—|–)\s*(.+))?$/);
                                                        if (m) {
                                                            const [, label, assetStr, freqStr] = m;
                                                            return (
                                                                <DocBulletItem
                                                                    key={itIdx}
                                                                    isSubItem={true}
                                                                    isMetaItem={true}
                                                                >
                                                                    <span>
                                                                        <strong style={{ color: '#38bdf8', fontWeight: 600, marginRight: '6px' }}>{label}</strong>
                                                                        <span style={{ color: '#cbd5e1' }}>{renderInlineMarkdown(assetStr, query)}</span>
                                                                        {freqStr && (
                                                                            <>
                                                                                <span style={{ margin: '0 6px', color: 'rgba(255, 255, 255, 0.3)' }}>—</span>
                                                                                <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>{renderInlineMarkdown(freqStr, query)}</span>
                                                                            </>
                                                                        )}
                                                                    </span>
                                                                </DocBulletItem>
                                                            );
                                                        }
                                                    }
                                                    return (
                                                        <DocBulletItem
                                                            key={itIdx}
                                                            isMicro={isMicro}
                                                            isSubItem={it.isSub || it.isSubSub}
                                                            isSubSub={it.isSubSub}
                                                            isActionItem={isActionItem}
                                                            isOptional={isOptional}
                                                            isRequired={isRequired}
                                                            isNumbered={it.isNumbered}
                                                            isChecklist={it.isChecklist}
                                                        >
                                                            {renderInlineMarkdown(it.text, query)}
                                                        </DocBulletItem>
                                                    );
                                                })}
                                            </DocBulletList>
                                        );
                                    }

                                    const ruleCardMatch = block.match(/^\s*\*\*(\d+)\s*[—–-]\s*([^*]+)\*\*(?::)?\s*([\s\S]*)/);
                                    if (ruleCardMatch) {
                                        const [, rNum, rHeading, rBody] = ruleCardMatch;
                                        return (
                                            <div
                                                key={bIdx}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'flex-start',
                                                    gap: '12px',
                                                    background: 'rgba(255, 255, 255, 0.02)',
                                                    border: '1px solid rgba(255, 255, 255, 0.07)',
                                                    borderRadius: '8px',
                                                    padding: '12px 14px',
                                                    margin: '8px 0',
                                                    width: '100%',
                                                    boxSizing: 'border-box'
                                                }}
                                            >
                                                <span style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    minWidth: '28px',
                                                    height: '28px',
                                                    borderRadius: '6px',
                                                    background: 'linear-gradient(135deg, rgba(56, 189, 248, 0.22), rgba(14, 165, 233, 0.22))',
                                                    border: '1px solid rgba(56, 189, 248, 0.45)',
                                                    color: '#38bdf8',
                                                    fontSize: '0.82rem',
                                                    fontWeight: '700',
                                                    flexShrink: 0,
                                                    marginTop: '1px'
                                                }}>
                                                    {rNum}
                                                </span>
                                                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                    <div style={{ fontSize: '0.94rem', fontWeight: '700', color: '#f1f5f9' }}>
                                                        {renderInlineMarkdown(rHeading.trim(), query)}
                                                    </div>
                                                    {rBody.trim() && (
                                                        <div style={{ fontSize: '0.88rem', lineHeight: '1.55', color: '#cbd5e1' }}>
                                                            {renderInlineMarkdown(rBody.trim(), query)}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    }

                                    return (
                                        <DocParagraph key={bIdx}>
                                            {renderInlineMarkdown(block, query)}
                                        </DocParagraph>
                                    );
                                })}
                            </div>
                        </DocSectionCard>
                    </React.Fragment>
                );
            })}
        </div>
    );
};

export default DocRichViewer;
